import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Divider,
  Tooltip,
} from "@mui/material";

const LucroRealConfig = ({ onAlert, onConfigChange, initialData }) => {
  const [configs, setConfigs] = useState({
    icmsFreteChecked: false,
    icmsFretePercent: "",
    pisCofinsSomarFreteComissao: false,
    diffal:
      "Será uma %(porcentagem) sobre o valor da venda, conforme o estado de destino.",
    icmsAliquota: "",
    pisCofins: "",
  });

  // Efeito para carregar dados do backend sempre que mudarem
  useEffect(() => {
    if (initialData) {
      const savedConfig = localStorage.getItem("tax_regime_config_lucro_real");
      const localConfig = savedConfig ? JSON.parse(savedConfig) : {};
      setConfigs((prev) => ({
        ...prev,
        ...localConfig,
        icmsAliquota: initialData.icmsAliquota || "",
        pisCofins: initialData.pisCofins || "",
        pisCofinsSomarFreteComissao:
          initialData.pisCofinsSellerFreightEnabled ||
          prev.pisCofinsSomarFreteComissao,
        icmsFreteChecked:
          initialData.icmsAliquotaSellerFreightEnabled || prev.icmsFreteChecked,
      }));
    }
  }, [initialData]);

  // Função para registrar cliques no localStorage (robusta)
  const handleFieldClick = (field) => {
    try {
      const key = `lucroRealConfig_clicks_${field}`;
      let count = 0;
      const stored = window.localStorage.getItem(key);
      if (stored !== null && !isNaN(Number(stored))) {
        count = Number(stored);
      }
      window.localStorage.setItem(key, String(count + 1));
    } catch (e) {}
  };
  const handleConfigChange = (field, value) => {
    const newConfigs = { ...configs, [field]: value };
    setConfigs(newConfigs);
    localStorage.setItem(
      "tax_regime_config_lucro_real",
      JSON.stringify(newConfigs)
    );
    // Notifica o componente pai sobre a alteração
    onConfigChange && onConfigChange();
    onAlert && onAlert("Configurações atualizadas com sucesso", "success");
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Card
        sx={{
          border: "1px solid #e8eaed",
          borderRadius: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: "#ff7043",
              display: "flex",
              alignItems: "center",
              gap: 2,
              fontSize: "1.2rem",
              borderBottom: "2px solid #fff3e0",
              paddingBottom: 2,
            }}
          >
            ⚙️ Configurações do Lucro Real
          </Typography>

          {/* Seção não editável - apenas informativa */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 3,
              mb: 3,
            }}
          >
            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "200px",
              }}
            >
              <TextField
                fullWidth
                label="ICMS Alíquota (%)"
                value={configs.icmsAliquota || ""}
                onChange={() => {}}
                type="number"
                size="small"
                helperText="Alíquota do ICMS configurada"
                disabled
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f8f9fa",
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "200px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <span>
                  <TextField
                    fullWidth
                    label="IRPJ (%)"
                    value="25"
                    onChange={() => {}}
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    size="small"
                    helperText="Imposto de Renda Pessoa Jurídica"
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#666",
                        mt: 0.5,
                      },
                    }}
                    disabled
                    onClick={() => handleFieldClick("irpjRate")}
                  />
                </span>
              </Tooltip>
            </Box>

            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "200px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <span>
                  <TextField
                    fullWidth
                    label="CSLL (%)"
                    value="9"
                    onChange={() => {}}
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    size="small"
                    helperText="Contribuição Social sobre Lucro"
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#666",
                        mt: 0.5,
                      },
                    }}
                    disabled
                    onClick={() => handleFieldClick("csllRate")}
                  />
                </span>
              </Tooltip>
            </Box>

            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "200px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <span>
                  <TextField
                    fullWidth
                    label="PIS (%)"
                    value="1.65"
                    onChange={() => {}}
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    size="small"
                    helperText="Programa de Integração Social"
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#666",
                        mt: 0.5,
                      },
                    }}
                    disabled
                    onClick={() => handleFieldClick("pisRate")}
                  />
                </span>
              </Tooltip>
            </Box>

            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "200px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <span>
                  <TextField
                    fullWidth
                    label="COFINS (%)"
                    value="7.6"
                    onChange={() => {}}
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    size="small"
                    helperText="Contribuição para Financiamento da Seguridade Social"
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#666",
                        mt: 0.5,
                      },
                    }}
                    disabled
                    onClick={() => handleFieldClick("cofinsRate")}
                  />
                </span>
              </Tooltip>
            </Box>

            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "200px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <span>
                  <TextField
                    fullWidth
                    label="Adicional IRPJ (%)"
                    value="10"
                    onChange={() => {}}
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    size="small"
                    helperText="Para lucros acima de R$ 240.000/ano"
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#666",
                        mt: 0.5,
                      },
                    }}
                    disabled
                    onClick={() => handleFieldClick("additionalIrpj")}
                  />
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Campos editáveis para regras do Lucro Real */}
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Seção PIS/COFINS */}
            <Box
              sx={{
                p: 3,
                bgcolor: "#fff3e0",
                borderRadius: 2,
                border: "1px solid #ffcc80",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: "#1a1a1a",
                  fontSize: "1rem",
                }}
              >
                PIS/COFINS
              </Typography>
              <Box>
                <Tooltip
                  title="Marque se deseja somar frete grátis e comissão no COFINS/crédito"
                  arrow
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      id="pisCofinsSomarFreteComissao"
                      checked={configs.pisCofinsSomarFreteComissao}
                      onChange={(e) =>
                        handleConfigChange(
                          "pisCofinsSomarFreteComissao",
                          e.target.checked
                        )
                      }
                      style={{ marginRight: 8, cursor: "pointer" }}
                    />
                    <label
                      htmlFor="pisCofinsSomarFreteComissao"
                      style={{
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        fontWeight: 500,
                        color: "#1a1a1a",
                        fontFamily:
                          '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    >
                      Somar frete grátis e comissão
                    </label>
                  </Box>
                </Tooltip>
              </Box>
            </Box>

            {/* Seção Difal */}
            <Box
              sx={{
                p: 3,
                bgcolor: "#f8f9fa",
                borderRadius: 2,
                border: "1px solid #e8eaed",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: "#1a1a1a",
                  fontSize: "1rem",
                }}
              >
                Difal
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    color: "#666",
                    fontSize: "0.9rem",
                    lineHeight: 1.5,
                    fontWeight: 500,
                  }}
                >
                  Será uma %(porcentagem) sobre o valor da venda, conforme o
                  estado de destino.
                </Typography>
              </Box>
            </Box>

            {/* Seção ICMS do Produto */}
            <Box
              sx={{
                p: 3,
                bgcolor: "#e8f5e8",
                borderRadius: 2,
                border: "1px solid #c8e6c9",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: "#1a1a1a",
                  fontSize: "1rem",
                }}
              >
                ICMS do Produto
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Tooltip
                  title="Marque se deseja somar frete grátis no ICMS do produto"
                  arrow
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      id="icmsFreteGratis"
                      checked={configs.icmsFreteChecked}
                      onChange={(e) =>
                        handleConfigChange("icmsFreteChecked", e.target.checked)
                      }
                      style={{ marginRight: 8, cursor: "pointer" }}
                    />
                    <label
                      htmlFor="icmsFreteGratis"
                      style={{
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        fontWeight: 500,
                        color: "#1a1a1a",
                        fontFamily:
                          '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    >
                      Somar frete grátis no ICMS do produto
                    </label>
                  </Box>
                </Tooltip>

                <TextField
                  fullWidth
                  label="ICMS Alíquota (%)"
                  type="number"
                  size="small"
                  value={configs.icmsAliquota || ""}
                  onChange={(e) =>
                    handleConfigChange("icmsAliquota", e.target.value)
                  }
                  placeholder="Ex: 12.00"
                  InputProps={{
                    endAdornment: (
                      <Typography variant="caption" color="text.secondary">
                        %
                      </Typography>
                    ),
                  }}
                  helperText="Alíquota do ICMS do produto"
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#666",
                      mt: 0.5,
                    },
                  }}
                  sx={{
                    mt: 1,
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#ff7043",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ff7043",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#ff7043",
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LucroRealConfig;
